import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { API_URL } from '../config/api';

const useDeviceType = () => {
  const getDevice = () => {
    const w = window.innerWidth;
    if (w <= 767) return 'mobile';
    if (w <= 1024) return 'tablet';
    return 'desktop';
  };
  const [device, setDevice] = useState(getDevice);
  useEffect(() => {
    const handleResize = () => setDevice(getDevice());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return device;
};

const UA_BLUE = '#0033A0';
const UA_DARK = '#001F6C';

const CallingPopup = () => {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [tfn, setTfn] = useState('');
  const [popupSettings, setPopupSettings] = useState({ desktop: true, mobile: true, tablet: true });
  const [popupContent, setPopupContent] = useState({ title: 'Exclusive Travel Deals', subtitle: 'Speak with our travel experts and get the best prices on flights, hotels & holiday packages.', features: ['✈ Lowest Fares Guaranteed', '🏨 Exclusive Holiday Packages', '🕐 Available 24/7', '💳 No Booking Fees'], buttonText: 'Call Now', imageUrl: '' });
  const device = useDeviceType();
  const location = useLocation();
  const isUal = location.pathname.startsWith('/ual/');

  const fetchSettings = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/public/site-settings`);
      const data = await res.json();
      if (data.success) {
        setTfn(data.settings.tfn || '');
        if (data.settings.popupSettings) setPopupSettings(data.settings.popupSettings);
        if (data.settings.popupContent) setPopupContent(prev => ({ ...prev, ...data.settings.popupContent }));
      }
    } catch (e) {
      console.error('CallingPopup: failed to fetch settings', e);
    }
  }, []);

  // Fetch settings once on mount
  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  // Page-specific TFN overrides
  const PAGE_TFN_OVERRIDES = {
    '/hotel/reservation': '1-844-480-0252',
  };
  const activeTfn = PAGE_TFN_OVERRIDES[location.pathname] || tfn;

  // Page-specific popup content overrides
  const PAGE_CONTENT_OVERRIDES = {
    '/hotel/reservation': {
      title: 'Hotel Reservation Support',
      subtitle: 'Speak with our hotel experts for new bookings, changes & cancellations.',
      features: ['🏨 Best Hotel Rates', '🛎 Exclusive Packages', '🕐 Available 24/7', '💳 No Booking Fees'],
    },
  };
  const activeContent = { ...popupContent, ...(PAGE_CONTENT_OVERRIDES[location.pathname] || {}) };

  // Reset popup on every route change so it shows on each page
  useEffect(() => {
    setVisible(false);
    setDismissed(false);
  }, [location.pathname]);

  useEffect(() => {
    if (dismissed) return;
    const isEnabled = isUal || popupSettings[device];
    if (!isEnabled) { setVisible(false); return; }
    const timer = setTimeout(() => setVisible(true), 3000);
    return () => clearTimeout(timer);
  }, [popupSettings, device, dismissed, location.pathname, isUal]);

    // Lock body scroll when popup is open
  useEffect(() => {
    if (visible) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [visible]);

  if (!visible || dismissed) return null;

  const handleDismiss = () => {
    setDismissed(true);
    setVisible(false);
  };

  const tfnClean = (activeTfn || '').replace(/[^0-9+]/g, '');

  // ── United Airlines branded popup for /ual/ routes ──
  if (isUal) {
    return (
      <div style={styles.overlay} onClick={handleDismiss}>
        <div style={{ ...styles.popup, borderRadius: '18px', overflow: 'hidden', maxWidth: '460px', padding: 0 }} onClick={e => e.stopPropagation()}>

          {/* UA Header */}
          <div style={{ background: 'linear-gradient(135deg, #0033A0 0%, #001F6C 100%)', padding: '18px 22px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '20px', fontFamily: 'Arial', fontWeight: 900, color: '#fff', letterSpacing: '2px' }}>UNITED</span>
              <span style={{ fontSize: '20px' }}>🌐</span>
            </div>
            <button style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '50%', width: '30px', height: '30px', color: '#fff', fontSize: '14px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={handleDismiss} aria-label="Close">✕</button>
          </div>

          {/* Body */}
          <div style={{ padding: '26px 28px 24px', textAlign: 'center', background: '#fff' }}>
            <div style={{ display: 'inline-block', background: '#e8f0fe', color: '#0033A0', fontSize: '11px', fontWeight: 700, letterSpacing: '0.8px', textTransform: 'uppercase', padding: '5px 14px', borderRadius: '20px', marginBottom: '14px' }}>
              United Airlines — Official Support
            </div>
            <h2 style={{ margin: '0 0 10px', fontSize: '22px', fontWeight: 800, color: '#0033A0', lineHeight: 1.2 }}>
              ✈️ Book United Airlines Flights
            </h2>
            <p style={{ margin: '0 0 18px', fontSize: '14px', color: '#555', lineHeight: 1.65 }}>
              Talk to a <strong>United Airlines specialist</strong> — get the best fares, manage bookings &amp; earn <strong>MileagePlus</strong> miles. Available <strong>24/7</strong>.
            </p>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'center', marginBottom: '20px' }}>
              {['✈️ Book & Change Flights', '🧳 Baggage Help', '⭐ MileagePlus', '📋 Manage Trips'].map((r, i) => (
                <span key={i} style={{ background: '#f0f4ff', border: '1px solid #c7d6f5', color: '#0033A0', fontSize: '12px', fontWeight: 600, padding: '6px 12px', borderRadius: '20px' }}>{r}</span>
              ))}
            </div>

            <a href={`tel:${tfnClean}`} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px', background: 'linear-gradient(135deg, #0033A0, #0055CC)', color: '#fff', borderRadius: '12px', padding: '16px 24px', textDecoration: 'none', marginBottom: '16px', boxShadow: '0 6px 20px rgba(0,51,160,0.45)' }} onClick={handleDismiss}>
              <span style={{ fontSize: '13px', fontWeight: 600, opacity: 0.88, letterSpacing: '0.5px' }}>📞 Call United Airlines Now</span>
              <span style={{ fontSize: '26px', fontWeight: 900, letterSpacing: '1.5px' }}>{activeTfn || 'Call Now'}</span>
            </a>

            <div style={{ display: 'flex', justifyContent: 'center', gap: '14px', flexWrap: 'wrap', marginBottom: '12px' }}>
              {['✔ Skip the hold', '✔ Instant booking', '✔ Free to call'].map((t, i) => (
                <span key={i} style={{ fontSize: '12px', color: '#2e7d32', fontWeight: 600 }}>{t}</span>
              ))}
            </div>
            <p style={{ margin: 0, fontSize: '10px', color: '#bbb', lineHeight: 1.6 }}>This is an independent travel service. United Airlines® is a registered trademark of United Airlines, Inc.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.overlay} onClick={handleDismiss}>
      <div style={styles.popup} onClick={e => e.stopPropagation()}>
        {/* Close button */}
        <button style={styles.closeBtn} onClick={handleDismiss} aria-label="Close popup">✕</button>

        {/* Top decorative bar */}
        <div style={styles.topBar} />

        {/* Content */}
        <div style={styles.content}>
          {/* Animated phone icon */}
          <div style={styles.iconWrap}>
            <div style={styles.iconRing} />
            <span style={styles.phoneIcon}>📞</span>
          </div>

          {activeContent.imageUrl && (
            <img src={activeContent.imageUrl} alt="Popup" style={{width:'100%',maxHeight:'180px',objectFit:'cover',borderRadius:'12px',marginBottom:'16px'}} />
          )}
          <h2 style={styles.title}>{activeContent.title || 'Exclusive Travel Deals'}</h2>
          <p style={styles.subtitle}>
            {activeContent.subtitle || 'Speak with our travel experts and get the best prices on flights, hotels & holiday packages.'}
          </p>

          {/* Features */}
          <div style={styles.features}>
            {(activeContent.features && activeContent.features.filter(Boolean).length > 0 ? activeContent.features : ['✈ Lowest Fares Guaranteed', '🏨 Exclusive Holiday Packages', '🕐 Available 24/7', '💳 No Booking Fees']).map((f, i) => (
              <div key={i} style={styles.featureItem}>{f}</div>
            ))}
          </div>

          {/* Call button */}
          <a href={`tel:${tfnClean}`} style={styles.callBtn} onClick={handleDismiss}>
            <span style={styles.callBtnIcon}>📞</span>
            {activeContent.buttonText || activeTfn || 'Call Now'}
          </a>

          <p style={styles.note}>Tap outside or press ✕ to close</p>
        </div>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 99999,
    background: 'rgba(0, 0, 0, 0.72)',
    backdropFilter: 'blur(6px)',
    WebkitBackdropFilter: 'blur(6px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px',
    animation: 'overlayFadeIn 0.3s ease',
  },
  popup: {
    position: 'relative',
    background: '#ffffff',
    borderRadius: '20px',
    width: '100%',
    maxWidth: '520px',
    maxHeight: '90vh',
    overflowY: 'auto',
    boxShadow: '0 24px 80px rgba(0,0,0,0.4)',
    animation: 'popupZoomIn 0.35s cubic-bezier(0.22,1,0.36,1)',
  },
  topBar: {
    height: '6px',
    borderRadius: '20px 20px 0 0',
    background: 'linear-gradient(90deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
  },
  closeBtn: {
    position: 'absolute',
    top: '16px',
    right: '16px',
    background: '#f0f0f0',
    border: 'none',
    color: '#333',
    fontSize: '16px',
    fontWeight: 700,
    cursor: 'pointer',
    borderRadius: '50%',
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1,
    zIndex: 1,
    transition: 'background 0.2s',
  },
  content: {
    padding: '32px 36px 36px',
    textAlign: 'center',
  },
  iconWrap: {
    position: 'relative',
    display: 'inline-block',
    marginBottom: '20px',
  },
  iconRing: {
    position: 'absolute',
    inset: '-8px',
    borderRadius: '50%',
    border: '3px solid #667eea',
    opacity: 0.3,
    animation: 'ringPulse 1.8s ease-in-out infinite',
  },
  phoneIcon: {
    fontSize: '52px',
    display: 'block',
    animation: 'phoneWiggle 1.2s ease-in-out infinite',
  },
  title: {
    margin: '0 0 10px',
    fontSize: '26px',
    fontWeight: 800,
    color: '#1a1a2e',
    letterSpacing: '-0.3px',
  },
  subtitle: {
    margin: '0 0 24px',
    fontSize: '15px',
    color: '#555',
    lineHeight: 1.6,
  },
  features: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '10px',
    marginBottom: '28px',
    textAlign: 'left',
  },
  featureItem: {
    background: '#f7f8ff',
    border: '1px solid #e8eaff',
    borderRadius: '10px',
    padding: '10px 12px',
    fontSize: '13px',
    fontWeight: 500,
    color: '#333',
  },
  callBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: '#fff',
    fontWeight: 800,
    fontSize: '20px',
    borderRadius: '50px',
    padding: '16px 32px',
    textDecoration: 'none',
    marginBottom: '14px',
    letterSpacing: '0.5px',
    boxShadow: '0 6px 24px rgba(102,126,234,0.5)',
    transition: 'transform 0.15s, box-shadow 0.15s',
  },
  callBtnIcon: {
    fontSize: '20px',
  },
  note: {
    margin: 0,
    fontSize: '12px',
    color: '#aaa',
  },
};

// Inject keyframes once
if (typeof document !== 'undefined') {
  const styleId = 'calling-popup-styles';
  if (!document.getElementById(styleId)) {
    const s = document.createElement('style');
    s.id = styleId;
    s.innerHTML = `
      @keyframes overlayFadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      @keyframes popupZoomIn {
        from { opacity: 0; transform: scale(0.88) translateY(20px); }
        to   { opacity: 1; transform: scale(1)    translateY(0);    }
      }
      @keyframes phoneWiggle {
        0%,100% { transform: rotate(0deg); }
        15%     { transform: rotate(-18deg); }
        30%     { transform: rotate(18deg); }
        45%     { transform: rotate(-10deg); }
        60%     { transform: rotate(10deg); }
        75%     { transform: rotate(-4deg); }
      }
      @keyframes ringPulse {
        0%,100% { transform: scale(1);   opacity: 0.3; }
        50%     { transform: scale(1.35); opacity: 0;   }
      }
      .calling-popup-call-btn:hover {
        transform: translateY(-2px);
        box-shadow: 0 10px 32px rgba(102,126,234,0.65) !important;
      }
    `;
    document.head.appendChild(s);
  }
}

export default CallingPopup;
